'use client';

import type { CSSProperties } from 'react';
import { VpIcon } from '@vtmn-play/react/headless';
import classNames from 'classnames';
import HTMLReactParser from 'html-react-parser';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

export const ProjectDescription = ({
  body,
  langValue,
  isHidden,
  handleClick,
}: {
  body: string;
  langValue: string;
  isHidden: boolean;
  handleClick: () => void;
}) => {
  const t = useTranslations('PROJECT');

  const [isOverflow, setIsOverflow] = useState(false);
  const [lineClampValue, setLineClampValue] = useState<number>();

  const contentRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef?.current || !outerRef?.current)
      return;

    const obs = new ResizeObserver(() => {
      const hasOverflow
        = (outerRef?.current?.offsetHeight ?? 0)
          < (contentRef?.current?.offsetHeight ?? 0);

      const divHeight = outerRef?.current?.offsetHeight ?? 0;
      const lineHeightFromComputedStyle = outerRef?.current && window.getComputedStyle(outerRef.current)?.lineHeight;
      const lineHeight = lineHeightFromComputedStyle ? Number.parseInt(lineHeightFromComputedStyle) : 1;
      const lines = divHeight / lineHeight;

      if (!isOverflow) {
        setIsOverflow(hasOverflow);
      }

      setLineClampValue(Math.floor(lines) || 1);
    });

    obs.observe(contentRef?.current);

    return () => obs.disconnect();
  }, [contentRef, outerRef, isOverflow]);

  return (
    <>
      <div
        ref={outerRef}
        className={classNames('vp-body-m project-detail__description', {
          'project-detail__description--un-shrunk': isOverflow && !isHidden,
        })}
        {...(isOverflow
          && isHidden && {
          style: {
            '--line-clamp': lineClampValue,
          } as CSSProperties,
        })}
      >
        <div
          ref={contentRef}
          className={classNames('project-detail__body', {
            'project-detail__body--clamped': isOverflow && isHidden,
          })}
          lang={langValue}
        >
          {body && HTMLReactParser(body)}
        </div>
      </div>
      {isOverflow && (
        <div className="project-detail__read-more">
          <button
            type="button"
            className="vp-link vp-link--small"
            onClick={handleClick}
          >
            {t('READ_MORE')}
            <VpIcon name={`chevron-${isHidden ? 'down' : 'up'}`} />
          </button>
        </div>
      )}
    </>
  );
};
